export type RbacScope = "staff" | "customer";
export type RbacPermission = {
  code: string;
  group: string;
  label: string;
  description: string;
  audience?: "staff" | "customer" | "both";
  locked?: boolean;
  scopes: RbacScope[];
  access: "read" | "write";
  grantable: boolean;
  disabledReason?: string;
};
export type RbacRole = {
  id: string;
  scope: RbacScope;
  customerId: string | null;
  shared?: boolean;
  code?: string;
  name: string;
  description: string;
  permissions: string[];
  builtin: boolean;
  locked: boolean;
  memberCount: number;
  assignable?: boolean;
  assignmentDisabledReason?: string;
};
export type RbacMember = {
  id: string;
  name: string;
  email: string;
  jobTitle?: string;
};
export type RbacView = {
  scope: RbacScope;
  customerId: string | null;
  customerName?: string;
  roles: RbacRole[];
  permissions: RbacPermission[];
  canManage: boolean;
  readOnlyReason?: string;
};
export type RbacRoleDraft = {
  id?: string;
  sourceRoleId?: string;
  scope: RbacScope;
  customerId: string | null;
  shared?: boolean;
  name: string;
  description: string;
  permissions: string[];
};
export type RbacExample =
  | "normal"
  | "empty"
  | "locked"
  | "readonly"
  | "in-use"
  | "last-admin"
  | "suspended"
  | "new"
  | "restricted"
  | "error";
const permission = (
  code: string,
  group: string,
  name: string,
  description: string,
  access: "read" | "write",
  scopes: RbacScope[] = ["staff"],
): RbacPermission => ({
  code,
  group,
  label: name,
  description,
  access,
  scopes,
  audience: scopes.length === 2 ? "both" : scopes[0],
  locked: false,
  grantable: true,
});
// Storybook fixture only. Live permissions and grantability are supplied by the server.
const catalog: RbacPermission[] = [
  permission(
    "CUSTOMER_READ",
    "고객사",
    "고객사 현황 보기",
    "고객사 기본 정보와 연결 현황을 봅니다.",
    "read",
    ["staff", "customer"],
  ),
  permission(
    "CUSTOMER_MANAGE",
    "고객사",
    "고객사 관리",
    "고객사를 등록하고 기본 정보를 바꿉니다.",
    "write",
  ),
  permission(
    "CUSTOMER_STATUS",
    "고객사",
    "서비스 상태 변경",
    "고객사의 이용 정지·재개를 관리합니다.",
    "write",
  ),
  permission(
    "CUSTOMER_SETTINGS",
    "고객사",
    "고객사 설정",
    "내 고객사의 알림과 기본 설정을 바꿉니다.",
    "write",
    ["staff", "customer"],
  ),
  permission(
    "MEMBER_READ",
    "회원",
    "회원 보기",
    "고객사 회원과 지정된 역할을 봅니다.",
    "read",
    ["staff", "customer"],
  ),
  permission(
    "MEMBER_MANAGE",
    "회원",
    "회원 관리",
    "회원을 초대하고 역할·이용 상태를 관리합니다.",
    "write",
    ["staff", "customer"],
  ),
  permission(
    "PII_REVEAL",
    "회원",
    "개인정보 원문 보기",
    "비밀번호를 다시 확인한 뒤 이름·연락처 원문을 봅니다. 열람은 감사 기록에 남습니다.",
    "read",
    ["staff", "customer"],
  ),
  permission(
    "USER_MANAGE",
    "회원",
    "관리자 계정 관리",
    "관리 콘솔의 관리자 계정을 등록하고 관리합니다.",
    "write",
  ),
  permission(
    "PROFILE_WRITE",
    "회원",
    "내 정보 수정",
    "내 이름·비밀번호·접속 세션을 관리합니다.",
    "write",
    ["staff", "customer"],
  ),
  permission(
    "MACHINE_PAIR",
    "머신",
    "머신 등록·해제",
    "연결 코드를 발급하고 머신 연결을 해제합니다.",
    "write",
  ),
  permission(
    "MACHINE_COMMAND",
    "머신",
    "머신 명령",
    "재시작·업데이트·동시 실행 수를 관리합니다.",
    "write",
  ),
  permission(
    "MACHINE_SANCTION",
    "머신",
    "머신 제재",
    "머신의 제재 단계와 해제를 관리합니다.",
    "write",
  ),
  permission(
    "DEVICE_WRITE",
    "디바이스",
    "디바이스 관리",
    "디바이스 이름·사용 상태·재연결을 관리합니다.",
    "write",
  ),
  permission(
    "TASK_DETAIL",
    "작업",
    "작업 상세 보기",
    "실행 상태·진행 로그·결과를 봅니다.",
    "read",
  ),
  permission(
    "TASK_CREATE",
    "작업",
    "작업 등록",
    "작업을 등록하고 예약·반복·승인을 관리합니다.",
    "write",
  ),
  permission(
    "TASK_CANCEL",
    "작업",
    "작업 취소·재시도",
    "등록한 작업을 취소하거나 다시 실행합니다.",
    "write",
  ),
  permission(
    "PUBLISH_READ",
    "작업",
    "발행 요청 보기",
    "내 고객사의 발행 요청과 결과를 봅니다.",
    "read",
    ["customer"],
  ),
  permission(
    "PUBLISH_WRITE",
    "작업",
    "발행 요청",
    "내 고객사의 글을 발행 요청하고 다시 제출합니다.",
    "write",
    ["customer"],
  ),
  permission(
    "MANUSCRIPT_READ",
    "원고",
    "원고함 보기",
    "원고·폴더·사용 이력을 봅니다.",
    "read",
  ),
  permission(
    "MANUSCRIPT_WRITE",
    "원고",
    "원고함 관리",
    "원고와 폴더를 만들고 편집·보관·삭제합니다.",
    "write",
  ),
  permission(
    "TEMPLATE_SYSTEM",
    "템플릿",
    "시스템 템플릿 관리",
    "공용 템플릿과 안전 규칙을 관리합니다.",
    "write",
  ),
  permission(
    "TEMPLATE_CUSTOMER",
    "템플릿",
    "고객사 템플릿 관리",
    "고객사별 템플릿을 등록하고 수정합니다.",
    "write",
  ),
  permission(
    "FILE_WRITE",
    "파일",
    "파일 올리기",
    "작업에 첨부할 파일을 올립니다.",
    "write",
  ),
  permission(
    "MARKETING_READ",
    "분석·SEO",
    "분석 보기",
    "키워드 순위·검색 노출·경쟁 분석을 봅니다.",
    "read",
    ["staff", "customer"],
  ),
  permission(
    "MARKETING_ASSET_WRITE",
    "분석·SEO",
    "분석 자산 관리",
    "추적 키워드·자산·분야를 등록하고 수정합니다.",
    "write",
  ),
  permission(
    "COLLECTOR_MANAGE",
    "분석·SEO",
    "수집기 관리",
    "검색 API 연결과 수집 실행을 관리합니다.",
    "write",
  ),
  permission(
    "USAGE_READ",
    "사용량",
    "사용량 보기",
    "고객사별 작업 수·토큰·사용 통계를 봅니다.",
    "read",
  ),
  permission(
    "BILLING_READ",
    "결제",
    "청구 보기",
    "청구서·결제 이력·요금제를 봅니다.",
    "read",
    ["staff", "customer"],
  ),
  permission(
    "BILLING_PAY",
    "결제",
    "결제·변경 신청",
    "청구서를 결제하고 요금제 변경을 신청합니다.",
    "write",
    ["staff", "customer"],
  ),
  permission(
    "BILLING_MANAGE",
    "결제",
    "요금제·청구 관리",
    "요금제·이용 한도·청구서를 조정합니다.",
    "write",
  ),
  permission(
    "AUDIT_READ",
    "감사",
    "고객사 감사 기록 보기",
    "내 고객사의 설정·회원 변경 이력을 봅니다.",
    "read",
    ["staff", "customer"],
  ),
  permission(
    "AUDIT_READ_ALL",
    "감사",
    "전체 감사 기록 보기",
    "모든 고객사의 변경 이력을 봅니다.",
    "read",
  ),
  permission(
    "ROLE_MANAGE",
    "역할·권한",
    "역할 관리",
    "현재 범위의 역할을 만들고 권한을 지정합니다.",
    "write",
    ["staff", "customer"],
  ),
  permission(
    "SYSTEM_READ",
    "시스템",
    "시스템 현황 보기",
    "관리 콘솔의 시스템 상태와 설정을 봅니다.",
    "read",
  ),
  permission(
    "MACHINE_DEPLOY",
    "시스템",
    "버전 배포",
    "Worker 배포본과 요구 버전을 관리합니다.",
    "write",
  ),
  permission(
    "LLM_KEY_MANAGE",
    "시스템",
    "AI API 키 관리",
    "AI 키·사용 모델·예산을 관리합니다.",
    "write",
  ),
];
export function rolesForScope(
  roles: RbacRole[],
  scope: RbacScope,
  customerId: string | null = null,
) {
  return roles.filter(
    (role) =>
      role.scope === scope &&
      (scope === "staff"
        ? role.customerId === null
        : (role.shared === true && role.customerId === null) ||
          (customerId !== null && role.customerId === customerId)),
  );
}
export function createRbacFixture(
  scope: RbacScope = "staff",
  customerId: string | null = "onyou",
  customerName = "온유의원",
): RbacView {
  const permissions = catalog
    .filter((p) => p.scopes.includes(scope))
    .map((p) => ({ ...p, scopes: [...p.scopes] }));
  const all = permissions.map((p) => p.code);
  const role = (
    id: string,
    name: string,
    description: string,
    permissionCodes: string[],
    memberCount: number,
    builtin = true,
    locked = false,
  ): RbacRole => ({
    id,
    name,
    description,
    permissions: [...permissionCodes],
    scope,
    customerId: scope === "customer" && !builtin ? customerId : null,
    shared: scope === "customer" && builtin,
    code: builtin
      ? (
          {
            "staff-admin": "SYSTEM_ADMIN",
            "staff-operator": "OPERATOR",
            "staff-billing": "BILLING_STAFF",
            "staff-viewer": "STAFF_VIEWER",
            "customer-owner": "CUSTOMER_OWNER",
            "customer-admin": "CUSTOMER_ADMIN",
            "customer-member": "CUSTOMER_MEMBER",
            "customer-viewer": "CUSTOMER_VIEWER",
          } as Record<string, string>
        )[id]
      : undefined,
    memberCount,
    builtin,
    locked,
  });
  const roles =
    scope === "staff"
      ? [
          role(
            "staff-admin",
            "시스템 관리자",
            "모든 관리 권한을 가진 기본 역할입니다.",
            all,
            1,
            true,
            true,
          ),
          role(
            "staff-operator",
            "운영",
            "고객사·작업·머신의 일상 운영을 담당합니다.",
            all.filter(
              (code) =>
                ![
                  "USER_MANAGE",
                  "PII_REVEAL",
                  "ROLE_MANAGE",
                  "LLM_KEY_MANAGE",
                  "BILLING_MANAGE",
                  "MACHINE_DEPLOY",
                ].includes(code),
            ),
            4,
          ),
          role(
            "staff-billing",
            "결제 담당",
            "청구서와 요금제, 고객사 이용 상태를 관리합니다.",
            [
              "CUSTOMER_READ",
              "CUSTOMER_STATUS",
              "BILLING_READ",
              "BILLING_MANAGE",
              "USAGE_READ",
              "AUDIT_READ_ALL",
            ],
            2,
          ),
          role(
            "staff-viewer",
            "열람",
            "운영 현황과 실행 결과를 읽기만 합니다.",
            ["CUSTOMER_READ", "TASK_DETAIL", "MARKETING_READ", "USAGE_READ"],
            3,
          ),
          role(
            "staff-analyst",
            "분석가",
            "고객사의 검색 성과와 원고를 함께 살펴봅니다.",
            [
              "CUSTOMER_READ",
              "MARKETING_READ",
              "MANUSCRIPT_READ",
              "USAGE_READ",
            ],
            2,
            false,
          ),
        ]
      : [
          role(
            "customer-owner",
            "소유자",
            "이 고객사의 모든 권한을 가진 기본 역할입니다.",
            all,
            1,
            true,
            true,
          ),
          role(
            "customer-admin",
            "관리자",
            "고객사의 회원·역할과 기본 설정을 관리합니다.",
            all.filter((code) => code !== "BILLING_PAY"),
            2,
          ),
          role(
            "customer-member",
            "멤버",
            "고객사 현황과 분석을 보고 발행을 요청합니다.",
            [
              "CUSTOMER_READ",
              "PROFILE_WRITE",
              "PUBLISH_READ",
              "PUBLISH_WRITE",
              "MARKETING_READ",
            ],
            4,
          ),
          role(
            "customer-viewer",
            "열람",
            "현황과 검색 성과를 읽기만 합니다.",
            ["CUSTOMER_READ", "PUBLISH_READ", "MARKETING_READ"],
            2,
          ),
          role(
            "customer-content",
            "콘텐츠 담당",
            "우리 고객사의 발행 요청과 검색 성과를 담당합니다.",
            [
              "CUSTOMER_READ",
              "PUBLISH_READ",
              "PUBLISH_WRITE",
              "MARKETING_READ",
            ],
            2,
            false,
          ),
        ];
  return {
    scope,
    customerId: scope === "customer" ? customerId : null,
    customerName: scope === "customer" ? customerName : undefined,
    roles:
      scope === "customer" && customerId === null
        ? roles.filter((item) => item.shared)
        : roles,
    permissions,
    canManage: true,
  };
}
export const rbacMemberFixture: RbacMember[] = [
  {
    id: "role-member-1",
    name: "이*진",
    email: "su***@metacrew.example",
    jobTitle: "팀장",
  },
  {
    id: "role-member-2",
    name: "정*호",
    email: "mi***@metacrew.example",
    jobTitle: "대리",
  },
];
